import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { Button, Input, Label, Card, CardHeader, CardTitle, CardContent } from '@/components/ui'
import { useAuth, type OwnerTenantMembership } from '@/lib/auth-store'
import { adminApi } from '@/lib/api'
import { Shield, User } from 'lucide-react'
import { cn } from '@/lib/cn'

export const Route = createFileRoute('/login')({
  beforeLoad: () => {
    if (useAuth.getState().isAuthenticated()) throw redirect({ to: '/' })
  },
  component: LoginPage,
})

type Tab = 'owner' | 'superadmin'

function LoginPage() {
  const navigate = useNavigate()
  const { apiUrl, setApiUrl } = useAuth()
  const [tab, setTab] = useState<Tab>('owner')

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Core Admin</CardTitle>
          <p className="text-sm text-neutral-400 mt-1">Iniciá sesión para administrar tu tienda.</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <Label htmlFor="url">API URL</Label>
              <Input
                id="url"
                type="url"
                value={apiUrl}
                onChange={(e) => setApiUrl(e.target.value)}
                placeholder="https://...workers.dev"
              />
            </div>

            <div className="grid grid-cols-2 gap-2 border-b border-neutral-800 -mx-6 px-6">
              <TabButton active={tab === 'owner'} onClick={() => setTab('owner')} icon={User}>
                Tienda
              </TabButton>
              <TabButton active={tab === 'superadmin'} onClick={() => setTab('superadmin')} icon={Shield}>
                Plataforma
              </TabButton>
            </div>

            {tab === 'owner' ? (
              <OwnerLoginForm onSuccess={() => navigate({ to: '/' })} />
            ) : (
              <SuperadminLoginForm onSuccess={() => navigate({ to: '/' })} />
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function TabButton({
  active,
  onClick,
  icon: Icon,
  children,
}: {
  active: boolean
  onClick: () => void
  icon: React.ComponentType<{ className?: string }>
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center justify-center gap-2 py-2.5 text-sm font-medium border-b-2 transition',
        active ? 'text-white border-blue-500' : 'text-neutral-500 border-transparent hover:text-neutral-300',
      )}
    >
      <Icon className="size-4" />
      {children}
    </button>
  )
}

interface MeResponse {
  data: {
    userId: string
    email: string
    name: string | null
    isSuperadmin: boolean
    tenants: OwnerTenantMembership[]
  }
}

function OwnerLoginForm({ onSuccess }: { onSuccess: () => void }) {
  const { apiUrl, setOwnerSession, setActiveTenant } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      // 1) Better-Auth signin → setea cookie de session
      const signinRes = await fetch(`${apiUrl.replace(/\/$/, '')}/api/auth/sign-in/email`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })
      if (!signinRes.ok) {
        const body = (await signinRes.json().catch(() => null)) as { message?: string } | null
        throw new Error(body?.message ?? 'Credenciales inválidas')
      }

      // 2) /me para conocer tenants accesibles
      const me = await adminApi.get<MeResponse>('/api/admin/me')
      if (me.data.tenants.length === 0) {
        throw new Error('Tu usuario no tiene tiendas asignadas. Contactá al administrador.')
      }

      setOwnerSession(me.data.email, me.data.tenants)

      // 3) Auto-seleccionar primer tenant
      const first = me.data.tenants[0]!
      setActiveTenant(first.tenantId, null)
      onSuccess()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 pt-2">
      <div>
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="vos@tutienda.com"
          required
        />
      </div>
      <div>
        <Label htmlFor="password">Contraseña</Label>
        <Input
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          required
        />
      </div>
      {error && (
        <div className="p-3 rounded-md bg-red-900/20 border border-red-800 text-sm text-red-400">{error}</div>
      )}
      <Button type="submit" disabled={loading} className="w-full">
        {loading ? 'Ingresando...' : 'Entrar'}
      </Button>
      <p className="text-xs text-neutral-500 text-center">
        ¿No tenés cuenta? Pedí a tu plataforma que te provisione una tienda.
      </p>
    </form>
  )
}

function SuperadminLoginForm({ onSuccess }: { onSuccess: () => void }) {
  const { apiUrl, setAdminToken } = useAuth()
  const [token, setToken] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(`${apiUrl.replace(/\/$/, '')}/api/admin/tenants`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error(`Token inválido (HTTP ${res.status})`)
      setAdminToken(token)
      onSuccess()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 pt-2">
      <div>
        <Label htmlFor="token">Admin Token</Label>
        <Input
          id="token"
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="ADMIN_BOOTSTRAP_TOKEN"
          required
        />
        <p className="text-xs text-neutral-500 mt-1">
          En el .env del core-api, copia ADMIN_BOOTSTRAP_TOKEN.
        </p>
      </div>
      {error && (
        <div className="p-3 rounded-md bg-red-900/20 border border-red-800 text-sm text-red-400">{error}</div>
      )}
      <Button type="submit" disabled={loading} className="w-full">
        {loading ? 'Verificando...' : 'Acceder como superadmin'}
      </Button>
    </form>
  )
}
